import { useCallback, useEffect, useState } from 'react';
import { HardDriveDownload, LogOut, Upload, Smartphone } from 'lucide-react';
import { AccountSwitcher, AppLauncher, ConfirmDialog, announceAccountsChanged, tabAccount, useAccountsChanged, useToast, type DeviceAccount } from '@dugzi/ui';
import { APP, api } from '../lib/api';
import type { Me } from '../lib/types';

let known: DeviceAccount[] = [];

/** The shared account switcher (D-028) and app launcher (D-056). */
export function AccountMenu({ me, onMove }: { me: Me; onMove(t: 'import' | 'export' | 'sync'): void }) {
  const toast = useToast();
  const [accounts, setAccounts] = useState<DeviceAccount[]>(known);
  const [confirmOut, setConfirmOut] = useState<null | 'one' | 'all'>(null);
  const load = useCallback(() => api<{ accounts: DeviceAccount[] }>('/api/accounts').then((r) => { known = r.accounts; setAccounts(r.accounts); }).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  useAccountsChanged(APP, load);
  const current: DeviceAccount = { id: me.user.id, name: me.user.name, address: me.user.email, accent: me.user.accent, space: me.user.space };
  const list = accounts.some((a) => a.id === current.id) ? accounts : [current, ...accounts];

  const switchTo = (a: DeviceAccount) => {
    tabAccount.set(APP, a.id);
    toast.show(`Now using ${a.address}`);
    window.location.assign('/');
  };

  async function signOut(all: boolean) {
    const targets = all ? list : [current];
    let redirect = '/';
    let remaining = 0;
    for (const a of targets) {
      const r = await api<{ redirect: string; remaining: number }>('/api/auth/logout', { body: { account: a.id } }).catch(() => null);
      if (r) { redirect = r.redirect; remaining = r.remaining; }
    }
    announceAccountsChanged(APP);
    if (all) redirect = redirect.replace(/account=[^&]+/, 'all=1');
    if (!all && remaining > 0) tabAccount.set(APP, null);
    window.location.assign(redirect);
  }

  return (
    <>
      <AppLauncher current="contacts" accountsUrl={me.accountsUrl} account={me.user.id} />
      <AccountSwitcher manageUrl={`${me.accountsUrl}/?account=${encodeURIComponent(me.user.id)}`}
        current={current}
        accounts={list}
        shortcuts
        onSwitch={switchTo}
        onOpenInNewTab={(a) => window.open(`/?account=${encodeURIComponent(a.id)}`, '_blank', 'noopener')}
        onAdd={() => window.location.assign(`/api/auth/login?add=1&return_to=${encodeURIComponent('/')}`)}
        onSignOut={() => setConfirmOut('one')}
        onSignOutAll={() => setConfirmOut('all')}
      >
        <button role="menuitem" className="dz-menu__item" onClick={() => onMove('import')} data-testid="menu-import"><Upload aria-hidden />Import a .vcf file</button>
        <button role="menuitem" className="dz-menu__item" onClick={() => onMove('export')} data-testid="your-data"><HardDriveDownload aria-hidden />Export your contacts</button>
        <button role="menuitem" className="dz-menu__item" onClick={() => onMove('sync')} data-testid="menu-sync"><Smartphone aria-hidden />Sync with your phone</button>
      </AccountSwitcher>
      <ConfirmDialog open={confirmOut === 'one'} onClose={() => setConfirmOut(null)} onConfirm={() => signOut(false)} title={list.length > 1 ? `Sign out of ${current.address}?` : 'Sign out of Dugzi?'} confirmLabel="Sign out" icon={<LogOut aria-hidden />}>
        <p className="dz-muted">{list.length > 1 ? 'Your other accounts stay signed in.' : 'You can sign in again any time.'}</p>
      </ConfirmDialog>
      <ConfirmDialog open={confirmOut === 'all'} onClose={() => setConfirmOut(null)} onConfirm={() => signOut(true)} title="Sign out of all accounts?" confirmLabel="Sign out of all" icon={<LogOut aria-hidden />}>
        <p className="dz-muted">Every account on this device signs out of Dugzi.</p>
      </ConfirmDialog>
    </>
  );
}
